import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, Award, Scissors } from "lucide-react";
import { COMPANY } from "../lib/constants";

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
                <div className="overline mb-4 fade-up">Om Fyr &amp; Finér</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-[#2C241B] max-w-4xl fade-up fade-up-delay-1">
                    Kvalitetshåndværk<br />
                    <span className="italic text-[#2F483A]">
                        uden kompromis.
                    </span>
                </h1>
            </section>

            {/* STORY + PORTRAIT */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-14">
                <div className="md:col-span-5">
                    <div className="img-hover overflow-hidden rounded-sm ring-1 ring-[#D1C7B7] aspect-[4/5]">
                        <img
                            src={COMPANY.portrait}
                            alt="Markus Dræby Ley"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="mt-5 overline">
                        Markus Dræby Ley · Sølvmedalje 2025
                    </div>
                </div>

                <div className="md:col-span-7 md:pt-6">
                    <div className="space-y-6 text-lg md:text-xl text-[#2C241B] leading-[1.7] font-light">
                        <p>
                            Tømrerfirmaet Fyr og Finér ApS drives af Markus
                            Ley — udlært tømrer fra Træfagenes
                            Byggeuddannelse, antaget med sølv, og med
                            fødderne plantet solidt i Nordsjælland.
                        </p>
                        <p>
                            Hos os er detaljen aldrig ligegyldig. Det er i
                            samlingerne, inddækningerne og de sidste
                            millimeter, at et stykke arbejde står og falder
                            — og det er dér, vi lægger det ekstra ind, selv
                            når ingen ser det.
                        </p>
                        <p>
                            Vi tager både de små og de større opgaver. Tag,
                            solceller, VELUX, franske døre, træterrasser,
                            renovering og tilbygninger. Det afgørende er
                            ikke opgavens størrelse — det er, at det bliver
                            lavet ordentligt første gang.
                        </p>
                        <p>
                            Når vi siger ja til en opgave, passer prisen,
                            datoen og kvaliteten. Telefonen tager vi selv —
                            her er der kort vej fra aftale til skrue i væg.
                        </p>
                        <p className="font-serif italic text-2xl text-[#2F483A] pt-2">
                            Alt andet er bare spån.
                        </p>
                        <p className="pt-2">
                            Fyr &amp; Finér er en del af Markus Ley Holding
                            ApS, med adresse i Stubmarken 28, 3230 Græsted.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-2 gap-8">
                        <div className="border-t border-[#2C241B]/15 pt-5">
                            <div className="overline mb-2">CVR</div>
                            <div className="font-serif text-2xl text-[#2C241B]">
                                {COMPANY.cvr}
                            </div>
                        </div>
                        <div className="border-t border-[#2C241B]/15 pt-5">
                            <div className="overline mb-2">Adresse</div>
                            <div className="font-serif text-2xl text-[#2C241B] leading-tight">
                                Stubmarken 28<br />
                                3230 Græsted
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SØLVMEDALJE — feature */}
            <section className="bg-[#2F483A] text-[#F9F6F0] mt-20 grain">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
                    <div className="md:col-span-6">
                        <div className="overline text-[#F9F6F0]/70 mb-4">
                            <Award size={12} className="inline mr-2 -mt-0.5" />
                            Svendeprøven 2025 · Antaget med sølv
                        </div>
                        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">
                            Sølvmedalje —<br />
                            <span className="italic">
                                og vi er ikke her for at være jævne.
                            </span>
                        </h2>
                        <div className="mt-10 space-y-5 text-[#F9F6F0]/85 font-light text-lg leading-relaxed">
                            <p>
                                Hos Tømrerfirmaet Fyr &amp; Finér skruer vi
                                ikke bare brædder sammen — vi bygger
                                håndværk, der kan mærkes.
                            </p>
                            <p>
                                Derfor er vi pavestolte over at kunne sige
                                det højt: sølvmedalje ved svendeprøven. Den
                                højeste udmærkelse i tømrerfaget. Ikke for
                                "næsten perfekt". Ikke for "god nok". Men
                                for det absolut skarpeste håndværk — dér,
                                hvor selv de gamle svende nikker
                                anerkendende.
                            </p>
                            <p>
                                Tømrerfaget er oldgammelt. Det stammer fra
                                en tid, hvor man lærte ved at se, prøve,
                                fejle og prøve igen. En tid hvor man
                                byggede huse, der skulle holde i
                                generationer. Det gør vi stadig. Bare med
                                lidt flere maskiner — og meget mere
                                attitude.
                            </p>
                            <p>
                                Medaljen er ikke bare et klap på skulderen.
                                Det er et bevis på, at vi gør tingene
                                ordentligt. At vi nørder detaljerne. At vi
                                giver alt.
                            </p>
                            <p className="font-serif italic text-2xl text-[#F9F6F0] pt-2">
                                Vi er kun lige begyndt. Vi bygger videre —
                                stærkere end nogensinde.
                            </p>
                            <p className="text-sm text-[#F9F6F0]/60 pt-2">
                                Tak til kunder, til Tømrerfirmaet Middelboe,
                                til lærerne — og til dem, der har delt en
                                kop kaffe eller to på værkstedet de kolde
                                morgener.
                            </p>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <div className="img-hover overflow-hidden rounded-sm ring-1 ring-[#F9F6F0]/10 aspect-[4/3] bg-[#1A2A21]">
                            <img
                                src={COMPANY.silverMedalImage}
                                alt="Sølvmedalje og svendebrev — Markus Dræby Ley, 26. september 2025"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="mt-4 flex items-center justify-between text-xs text-[#F9F6F0]/55 uppercase tracking-[0.2em]">
                            <span>Træfagenes Byggeuddannelse</span>
                            <span>26. september 2025</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* KØDSNEDKER — fun fact */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
                <div className="md:col-span-4">
                    <div className="overline mb-3">
                        <Scissors size={12} className="inline mr-2 -mt-0.5" />
                        Fun fact
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C241B]">
                        En ægte<br />
                        <span className="italic text-[#8B5E3C]">
                            kødsnedker.
                        </span>
                    </h2>
                </div>
                <div className="md:col-span-8 md:pt-4 space-y-5 text-lg text-[#2C241B] font-light leading-[1.7]">
                    <p>
                        Udover at være tømrer med kærlighed til træet, er
                        Markus faktisk også udlært detailslagter — helt
                        tilbage i 2007. Bestået med ros, knivskarpe snit og
                        præcision i både kød og konstruktion.
                    </p>
                    <p>
                        Det lyder som et sjovt sammentræf, men i
                        virkeligheden er det samme fag: at måle op, skære
                        rent, og aldrig gå på kompromis — hvad enten det er
                        en udskæring, en lige samling eller godt håndværk
                        generelt.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
                        {[
                            ["Detailslagter", "Udlært 2007"],
                            ["Tømrer", "Udlært 2025 · Sølvmedalje"],
                            ["Kødsnedker", "Aldrig på kompromis"],
                        ].map(([t, d]) => (
                            <div
                                key={t}
                                className="pt-5 border-t border-[#2C241B]/15"
                            >
                                <div className="font-serif text-xl text-[#2C241B] mb-1">
                                    {t}
                                </div>
                                <div className="text-sm text-[#7A6855]">
                                    {d}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* VALUES */}
            <section className="bg-[#EAE3D5]/60 border-y border-[#D1C7B7]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <div className="overline mb-3">Sådan arbejder vi</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#2C241B]">
                            Detaljerne<br />skaber helheden.
                        </h2>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                n: "01",
                                t: "Detaljen",
                                d: "I samlingerne, inddækningerne og de sidste millimeter står og falder et stykke arbejde. Det er dér, vi lægger det ekstra ind.",
                            },
                            {
                                n: "02",
                                t: "Ordet",
                                d: "Når vi aftaler en pris og en dato, passer det. Ring til os direkte — vi er hellere 10 minutter på telefonen end 3 uger bagud.",
                            },
                            {
                                n: "03",
                                t: "Løsningen",
                                d: "Kan det ikke på den måde, kan det på en anden. Vi er altid klar på nye løsninger og smarte idéer.",
                            },
                        ].map((v) => (
                            <div
                                key={v.n}
                                className="pt-6 border-t border-[#2C241B]/25"
                            >
                                <div className="font-serif text-5xl text-[#8B5E3C] mb-4">
                                    {v.n}
                                </div>
                                <div className="font-serif text-2xl text-[#2C241B] mb-3">
                                    {v.t}
                                </div>
                                <p className="text-[#4A3B2C] font-light">
                                    {v.d}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
                <h2 className="font-serif text-4xl md:text-6xl leading-tight text-[#2C241B] max-w-2xl">
                    Har du et projekt vi skal<br />
                    <span className="italic text-[#2F483A]">
                        tage fat i sammen?
                    </span>
                </h2>
                <div className="flex flex-col gap-4">
                    <a
                        href={COMPANY.phoneHref}
                        data-testid="about-phone-link"
                        className="inline-flex items-center gap-2 bg-[#2F483A] text-[#F9F6F0] px-8 py-4 text-xs uppercase tracking-[0.22em] rounded-sm hover:bg-[#223429] transition-colors"
                    >
                        <Phone size={14} /> {COMPANY.phone}
                    </a>
                    <a
                        href={COMPANY.emailHref}
                        data-testid="about-email-link"
                        className="inline-flex items-center gap-2 border border-[#2C241B]/40 hover:border-[#2C241B] px-8 py-4 text-xs uppercase tracking-[0.22em] rounded-sm text-[#2C241B] transition-colors"
                    >
                        <Mail size={14} /> {COMPANY.email}
                    </a>
                    <Link
                        to="/projekter"
                        data-testid="about-projects-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#2C241B] link-reveal"
                    >
                        Se projekter <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
